import React, { Suspense } from "react";
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import App from "./App";
import { store } from "./store/store";
import PersistProvider from "./store/providers/persist-provider";
import { setProducts } from "./store/slices/product-slice";
import 'animate.css';
import 'swiper/swiper-bundle.min.css';
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";
import "./assets/scss/style.scss";
import "./i18n";
import { initGA } from "./helpers/analytics";

initGA();

// Loading component
const LoadingScreen = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    fontSize: '1.5rem'
  }}>
    Loading products...
  </div>
);

// Main app wrapper that handles initialization
const AppWrapper = ({ children }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const initializeApp = async () => {
      try {
        const apiBaseUrl = process.env.REACT_APP_API_URL || 'https://ca816ed0-67a5-454a-b749-5b5632ff4c5d.clouding.host';

        // Load TronLogo config first so window.__tl.sid is set before product grid renders
        try {
          const tlRes = await fetch(`${apiBaseUrl}/api/tronlogo/config`);
          console.log('[TronLogo config] response status:', tlRes.status);
          console.log(tlRes);
          if (tlRes.ok) {
            const { api_key, sid } = await tlRes.json();
            console.log('[TronLogo config] sid:', sid, 'api_key present:', !!api_key, api_key ? `(${api_key.slice(0, 4)}...)` : '');
            window.__tl = { sid };
            if (api_key) {
              const s = document.getElementById('tronlogo-script');
              console.log('[TronLogo config] #tronlogo-script element found:', !!s);
              if (s) {
                s.src = `https://app.promotron.com/promotron.js?api-key=${api_key}&lang=es`;
                s.async = true;
                console.log('[TronLogo config] promotron.js src set to:', s.src);
              }
            } else {
              console.warn('[TronLogo config] no api_key returned — promotron.js will not be loaded');
            }
          } else {
            console.error('[TronLogo config] request failed with status', tlRes.status);
          }
        } catch (err) {
          console.error('[TronLogo config] failed:', err);
        }

        const response = await fetch(`${apiBaseUrl}/api/products/bypass/Creoo`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const products = await response.json();
        console.log('Products loaded:', products.length, 'items');

        store.dispatch(setProducts(products));

        setIsLoading(false);
      } catch (err) {
        console.error('Failed to initialize app:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: 'red',
        fontSize: '1.2rem'
      }}>
        Error loading products: {error}
      </div>
    );
  }

  // Content pages don't need the product list: render them right away so crawlers
  // (and users) aren't stuck on the loading screen while the API responds.
  const path = window.location.pathname.replace(/\/$/, '');
  const isContentPage = /^\/(blog(\/.*)?|about|contact|contacto|aviso-legal|cookie-policy)$/.test(path);

  if (isLoading && !isContentPage) {
    return <LoadingScreen />;
  }

  return children;
};

const container = document.getElementById('root');
const root = createRoot(container);

root.render(
  <HelmetProvider>
    <Provider store={store}>
      <PersistProvider>
        <AppWrapper>
          <App />
        </AppWrapper>
      </PersistProvider>
    </Provider>
  </HelmetProvider>
);
